import { sum } from 'radash';

export interface MatchResult {
  score: number;
  chunks: string[];
}

export interface PartialMatchResult extends MatchResult {
  rest: string;
}

export const textMatch = (text: string, query: string): MatchResult | undefined => {
  const { score, chunks, rest } = partialTextMatch(text, query);

  if (rest.length) {
    return undefined;
  }

  return { score, chunks };
};

export const partialTextMatch = (text: string, query: string): PartialMatchResult => {
  if (query.length === 0) {
    return { score: 1, chunks: [text], rest: '' };
  }

  if (text.length === 0) {
    return { score: 0, chunks: [], rest: query };
  }

  const lowerText = text.toLowerCase();

  const chunks: string[] = [];
  let qi = 0;
  let ti = 0;
  let rest = '';

  for (const q of query.toLowerCase()) {
    const next = lowerText.indexOf(q, ti);

    if (next === -1) {
      chunks.push(text.slice(ti));
      rest = query.slice(qi);
      return { score: 0, chunks, rest };
    }

    if (ti === 0 || next > ti) {
      chunks.push(text.slice(ti, next));
      chunks.push('');
    }

    chunks[chunks.length - 1] += text[next]!;

    qi++;
    ti = next + 1;
  }

  chunks.push(text.slice(ti));

  // Prefer matches that are less fragmented
  const fragmentationScore = 1 / Math.floor(chunks.length / 2);

  // Prefer matches have less space between matching chunks
  const distanceScore =
    1 -
    sum(
      chunks
        .slice(1, -1)
        .filter((_, i) => i % 2 === 1)
        .map((x) => x.length),
    ) /
      text.length;

  // Prefer matches that cover more of the text
  const lengthScore = query.length / text.length;

  const score = Math.max(0.8 * fragmentationScore + 0.1 * distanceScore + 0.1 * lengthScore, Number.MIN_VALUE);

  return { score, chunks, rest };
};
